import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, EnvelopeSimple, MapPin, Phone, Check } from "@phosphor-icons/react";

const SERVICE_OPTIONS = [
  "Web & Mobile Engineering",
  "Cloud & DevOps",
  "AI / ML & Data",
  "Cybersecurity",
  "UI / UX Design",
  "IT Consulting & Staffing",
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", company: "", service: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const validEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

  const encodeFormData = (data) =>
    Object.keys(data)
      .map((k) => `${encodeURIComponent(k)}=${encodeURIComponent(data[k])}`)
      .join("&");

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.name.trim() || !validEmail(form.email) || form.message.trim().length < 5) {
      toast.error("Please fill in name, a valid email, and a short message.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: encodeFormData({ "form-name": "contact", ...form }),
      });
      if (!res.ok) throw new Error(`Submission failed with status ${res.status}`);
      setSubmitted(true);
      toast.success("Message sent. We'll respond within 24 hours.");
      setForm({ name: "", email: "", company: "", service: "", message: "" });
    } catch (err) {
      toast.error("Couldn't send your message. Please try again or email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* SPLIT HERO + FORM */}
      <section className="border-b border-black/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-20 pb-24 grid grid-cols-12 gap-10 md:gap-16">
          {/* LEFT */}
          <div className="col-span-12 md:col-span-5">
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#FF4F00] reveal-up">/ Contact</p>
            <h1 className="font-display mt-6 text-5xl sm:text-6xl md:text-7xl font-black uppercase tracking-tighter leading-[0.9] reveal-up">
              Let's<br/>build<br/><span className="text-[#FF4F00]">something.</span>
            </h1>
            <p className="mt-8 text-[#52525B] leading-relaxed max-w-md reveal-up" style={{animationDelay: '.1s'}}>
              Drop us a line about your team, your roadmap, and what success looks like. A squad lead replies within 24 business hours.
            </p>

            <div className="mt-12 space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 border border-black/20 inline-flex items-center justify-center"><EnvelopeSimple size={18} weight="bold" /></div>
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#52525B]">Email</p>
                  <a href="mailto:contactus@ucubetechnologies.com" data-testid="contact-email-link" className="block mt-1 font-display text-lg font-bold hover:text-[#FF4F00]">contactus@ucubetechnologies.com</a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 border border-black/20 inline-flex items-center justify-center"><Phone size={18} weight="bold" /></div>
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#52525B]">Phone</p>
                  <a href="tel:+918000000000" data-testid="contact-phone-link" className="block mt-1 font-display text-lg font-bold hover:text-[#FF4F00]">+91 78 4099 4774</a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 border border-black/20 inline-flex items-center justify-center"><MapPin size={18} weight="bold" /></div>
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#52525B]">Pune Studio</p>
                  <p className="mt-1 font-display text-lg font-bold">Plot No.7, Servery No. 56, Gulmohar Park, Hadapsar, MH 411028</p>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT FORM */}
          <div className="col-span-12 md:col-span-7">
            <div className="bg-[#0A0A0A] text-white p-8 md:p-12 relative overflow-hidden">
              <div className="absolute inset-0 bg-grid-dark opacity-30 pointer-events-none" />
              <div className="relative">
                <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#FF4F00]">/ Brief</p>
                <h2 className="font-display mt-3 text-3xl md:text-4xl font-extrabold uppercase tracking-tight">Tell us about your project.</h2>

                {submitted ? (
                  <div data-testid="contact-success" className="mt-10 border border-[#FF4F00] p-10">
                    <div className="w-14 h-14 inline-flex items-center justify-center bg-[#FF4F00]"><Check size={28} weight="bold" /></div>
                    <h3 className="font-display mt-6 text-2xl font-bold uppercase">Message sent.</h3>
                    <p className="mt-3 text-white/70 max-w-md">Your brief was sent straight to our team. A squad lead will reach out within 24 business hours.</p>
                    <button
                      data-testid="contact-reset-button"
                      onClick={() => setSubmitted(false)}
                      className="mt-8 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-[#FF4F00] hover:text-white link-underline"
                    >
                      Send another message <ArrowRight size={14} weight="bold" />
                    </button>
                  </div>
                ) : (
                  <form
                    onSubmit={onSubmit}
                    name="contact"
                    data-netlify="true"
                    netlify-honeypot="bot-field"
                    className="mt-10 space-y-8"
                    data-testid="contact-form"
                  >
                    <input type="hidden" name="form-name" value="contact" />
                    <p className="hidden">
                      <label>
                        Don't fill this out if you're human: <input name="bot-field" />
                      </label>
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <Field label="Full Name *" name="name" value={form.name} onChange={onChange} placeholder="Jane Doe" required testid="contact-input-name" />
                      <Field label="Work Email *" name="email" type="email" value={form.email} onChange={onChange} placeholder="jane@company.com" required testid="contact-input-email" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <Field label="Company" name="company" value={form.company} onChange={onChange} placeholder="Acme Corp" testid="contact-input-company" />
                      <div>
                        <label className="text-xs font-bold uppercase tracking-[0.25em] text-white/60 mb-2 block">Service of Interest</label>
                        <select
                          name="service"
                          value={form.service}
                          onChange={onChange}
                          data-testid="contact-input-service"
                          className="w-full border-b-2 border-white/20 bg-transparent py-4 text-base focus:border-[#FF4F00] outline-none rounded-none text-white"
                        >
                          <option value="" className="text-black">Select a service</option>
                          {SERVICE_OPTIONS.map((s) => (
                            <option key={s} value={s} className="text-black">{s}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-bold uppercase tracking-[0.25em] text-white/60 mb-2 block">Project Brief *</label>
                      <textarea
                        name="message"
                        value={form.message}
                        onChange={onChange}
                        rows={5}
                        required
                        placeholder="Tell us about your roadmap, stack, and what you need help with…"
                        data-testid="contact-input-message"
                        className="w-full border-b-2 border-white/20 bg-transparent py-4 text-base focus:border-[#FF4F00] outline-none rounded-none text-white placeholder:text-white/30 resize-none"
                      />
                    </div>

                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6 pt-4">
                      <p className="text-[11px] text-white/40 uppercase tracking-[0.2em] max-w-xs">We respect your inbox. No newsletters, no spam, no third parties.</p>
                      <button
                        type="submit"
                        disabled={submitting}
                        data-testid="contact-submit-button"
                        className="inline-flex items-center gap-3 bg-[#FF4F00] text-white px-8 py-5 text-sm font-bold uppercase tracking-[0.2em] hover:bg-[#E64700] disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {submitting ? "Sending…" : "Send Brief"} <ArrowRight size={18} weight="bold" />
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ STRIP */}
      <section className="border-b border-black/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#FF4F00]">/ FAQ</p>
          <h2 className="font-display mt-4 text-4xl sm:text-5xl font-extrabold uppercase tracking-tight">Common questions.</h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-px bg-black/10">
            {[
              { q: "How fast do you start?", a: "Most engagements kick off within 7–10 business days after the brief is signed." },
              { q: "Do you offer fixed-price work?", a: "We default to time-and-materials with weekly burn caps. Fixed scope is available for discrete projects." },
              { q: "Where is your team based?", a: "Studios in Bengaluru and Singapore. Engineers across 11 countries." },
              { q: "Do you sign NDAs?", a: "Yes, before the first technical conversation if you prefer." },
            ].map((f) => (
              <div key={f.q} className="bg-white p-8">
                <p className="font-display text-xl font-bold">{f.q}</p>
                <p className="mt-3 text-[#52525B] leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, testid, ...props }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-[0.25em] text-white/60 mb-2 block">{label}</label>
      <input
        {...props}
        data-testid={testid}
        className="w-full border-b-2 border-white/20 bg-transparent py-4 text-base focus:border-[#FF4F00] outline-none rounded-none text-white placeholder:text-white/30"
      />
    </div>
  );
}
